import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormHeader } from './FormHeader';
import { FormError } from './FormError';
import { SubmitButton } from './SubmitButton';
import { PasswordField } from './PasswordField';
import { PasswordRules } from './PasswordRules';
import { useResetPasswordForm } from '../hooks/useResetPasswordForm';

/*
 * No `required` asterisks on the auth surfaces — a deliberate design choice for
 * the signed-out experience, NOT a general "all fields required" rule.
 * Everywhere inside the application, required fields DO carry the asterisk,
 * whether or not the form also has optional fields.
 */
export function ResetPasswordForm() {
  const { t } = useTranslation();
  const { form, onSubmit, isSubmitting, error, password, hasToken, canSubmit } =
    useResetPasswordForm();

  return (
    <div className="w-full">
      <FormHeader
        title={t('auth.resetPasswordTitle')}
        subtitle={t('auth.resetPasswordDescription')}
      />

      <FormError message={!hasToken ? t('auth.invalidResetToken') : error} />

      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.newPassword')}</FormLabel>
                <FormControl>
                  <PasswordField
                    placeholder="••••••••"
                    autoComplete="new-password"
                    disabled={!hasToken}
                    toggleLabel={t('auth.togglePasswordVisibility')}
                    {...field}
                  />
                </FormControl>
                {/*
                  No <FormMessage /> here, deliberately: <PasswordRules> below
                  renders a LIVE checklist of the same five rules, so a message
                  would state twice what is already on screen. confirmPassword
                  keeps its FormMessage because "passwords do not match" is the
                  one error the checklist cannot express.
                */}
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.confirmNewPassword')}</FormLabel>
                <FormControl>
                  <PasswordField
                    placeholder="••••••••"
                    autoComplete="new-password"
                    disabled={!hasToken}
                    toggleLabel={t('auth.togglePasswordVisibility')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <PasswordRules password={password} />

          <SubmitButton loading={isSubmitting} disabled={!canSubmit}>
            {t('auth.resetPassword')}
          </SubmitButton>
        </form>
      </Form>
    </div>
  );
}
